import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare, ChevronDown } from "lucide-react";

const DISMISS_KEY = "sl_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isIos() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as MacIntel with touch
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export default function InstallPwaPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    try {
      const last = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (last && Date.now() - last < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    } catch (_e) { /* ignore */ }

    // iOS: no beforeinstallprompt — show manual instructions automatically after a short delay.
    if (isIos()) {
      const t = setTimeout(() => { setIos(true); setVisible(true); }, 2500);
      return () => clearTimeout(t);
    }

    // Android / desktop Chrome: use the native install prompt event.
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") setVisible(false);
    } catch (_e) { /* ignore */ }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  // ---- iOS: guided prompt anchored to the bottom, with an animated arrow
  // pointing to Safari's Share button (Apple forbids any 1-tap install API). ----
  if (ios) {
    return (
      <div className="fixed inset-x-3 bottom-3 z-[70]" data-testid="pwa-install-prompt">
        <div className="relative sl-card !p-4 shadow-2xl border-2 border-[var(--sl-primary)]/25">
          <button onClick={dismiss} aria-label="Fermer" className="absolute top-2 right-2 text-gray-400 hover:text-gray-700" data-testid="pwa-dismiss">
            <X size={16} />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center flex-shrink-0">
              <Download size={20} className="text-[var(--sl-primary)]" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold text-sm">Installer l&apos;application</div>
              <p className="text-xs text-gray-600 mt-0.5">Accédez à Swiftlink en 1 geste depuis votre écran d&apos;accueil.</p>
              <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-800 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] font-semibold rounded-lg px-2 py-1">
                  <Share size={14} /> Partager
                </span>
                <ChevronDown size={14} className="text-gray-400 -rotate-90" />
                <span className="inline-flex items-center gap-1 bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] font-semibold rounded-lg px-2 py-1">
                  <PlusSquare size={14} /> Sur l&apos;écran d&apos;accueil
                </span>
              </div>
              <button onClick={dismiss} className="mt-3 text-xs text-gray-500 hover:text-gray-700" data-testid="pwa-later">J&apos;ai compris</button>
            </div>
          </div>
          {/* Bouncing arrow pointing down to Safari's share button */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-9 flex flex-col items-center pointer-events-none" data-testid="pwa-ios-arrow">
            <span className="text-[10px] font-semibold text-[var(--sl-primary)] bg-white/90 px-2 py-0.5 rounded-full shadow-sm mb-0.5 animate-pulse">
              Appuyez ici
            </span>
            <ChevronDown size={30} strokeWidth={3} className="text-[var(--sl-primary)] animate-bounce drop-shadow" />
          </div>
        </div>
      </div>
    );
  }

  // ---- Android / desktop Chrome: real 1-tap native install ----
  return (
    <div className="fixed bottom-28 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[60] sl-card !p-4 shadow-2xl" data-testid="pwa-install-prompt">
      <button onClick={dismiss} aria-label="Fermer" className="absolute top-2 right-2 text-gray-400 hover:text-gray-700" data-testid="pwa-dismiss">
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center flex-shrink-0">
          <Download size={18} className="text-[var(--sl-primary)]" />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-sm">Installer Swiftlink-Africa</div>
          <p className="text-xs text-gray-600 mt-0.5">Accédez à vos transferts en un clic, même hors ligne.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={install} className="sl-btn-primary !py-1.5 !px-4 !text-xs" data-testid="pwa-install">Installer</button>
            <button onClick={dismiss} className="text-xs text-gray-500 hover:text-gray-700 px-2" data-testid="pwa-later">Plus tard</button>
          </div>
        </div>
      </div>
    </div>
  );
}
